import { useEffect, useState } from 'react'
import useSWR from 'swr'

import { useRootContext } from '@frame/lib/context'

export enum ItemKind {
  EMPLOYER = 'employer',
  PLATFORM = 'platform',
  GIG = 'gig',
  BENEFITS = 'benefits',
  INCOME = 'income',
}

export enum ItemStatus {
  HEALTHY = 'healthy',
  ISSUES = 'issues',
  UNAVAILABLE = 'unavailable',
}

export enum ItemMappingStatus {
  VERIFIED = 'verified',
  MAPPED = 'mapped',
  UNMAPPED = 'unmapped',
}

export type Item = {
  id: string
  name: string
  kind: ItemKind
  known_limitations?: string | null
  status: ItemStatus
  status_details?: string
  mapping_status?: ItemMappingStatus
  logo_url?: string
  is_grouping?: boolean
  matching_score?: string
  detail?: string
}

export type ItemsResponse = {
  next: null
  previous: null
  results: Item[]
}

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function useGetItems({ query, kinds, limit }: { query: string; kinds?: ItemKind[]; limit?: number }): {
  items: Item[]
  error: Error
  isLoading: boolean
  isValidating: boolean
} {
  const debouncedQuery = useDebounce(query, 250)

  const { api } = useRootContext()
  const { data, error, isLoading, isValidating } = useSWR(
    ['companySearch', debouncedQuery, kinds, limit],
    async () => await api.common.companySearch(debouncedQuery, kinds, limit),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
    }
  )

  if (!debouncedQuery) {
    return {
      items: [],
      error,
      isLoading: true,
      isValidating: false,
    }
  }

  const items: Item[] = data?.data?.results ?? []

  return {
    items,
    error,
    isLoading,
    isValidating,
  }
}
